import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CoreProvider } from '../core.provider';
import { LoginRequestDto, SessionDto } from '../api/models';
import { JwtPayload, SessionDtoExtended } from './session-dto-extended';
import { Mutex } from './mutex';
import { setTag, setUser } from '@sentry/angular';

@Injectable({
  providedIn: "root",
})
export class AuthService {

  public async getLiveTokens(): Promise<SessionDtoExtended | undefined> {
    const tokensData = await this.core.storage.getItem('live-tokens');
    if (!tokensData) return undefined;
    try {
      return JSON.parse(tokensData);
    } catch (err) {
      console.error('Error parsing stored live tokens', err);
      return undefined;
    }
  }

  private userData?: JwtPayload | null = null;
  public get user(): JwtPayload | undefined {
    return this.userData || undefined;
  }
  public get isAuthenticated(): boolean | null {
    if (this.userData === null) return null;
    return !!this.userData;
  }

  /**
   * Whether to show the internals — worker names, pipeline logs, raw errors.
   *
   * This is a **presentation** check, not a security boundary: the API decides
   * what it hands out. It exists because those fields are ours, not the user's:
   * they are in Spanish, they name the stages of the pipeline, and a raw
   * traceback tells a customer nothing they can act on.
   */
  public get isSuperadmin(): boolean {
    return this.userData?.role === 'SUPER_ADMIN';
  }

  private core!: CoreProvider;
  async initCore(core: CoreProvider) {
    this.core = core;
    await this.initialiseAuth();
  }

  async initialiseAuth() {
    const tokens = await this.getLiveTokens();
    if (tokens) {
      this.userData = tokens.accessTokenParsed;
      setUser({
        email: this.userData.email,
        name: this.userData.name,
        role: this.userData.role,
        id: this.userData.sub,
      });
      setTag('user.id', this.userData.sub);
      setTag('user.display', `${this.userData.name} (${this.userData.email})`);
      setTag('platform', 'web');
    } else {
      this.userData = undefined;
      return;
    }
  }

  async login(login: LoginRequestDto): Promise<SessionDtoExtended> {
    const sess = await firstValueFrom(this.core.api.auth.authControllerLogin({body: login})); // Can throw an error to caller
    return this.adoptSession(sess);
  }

  /**
   * Takes a session issued by the API and becomes it.
   *
   * Split out of `login()` because impersonation gets a real session from
   * `/superadmin/users/:uuid/impersonate` without ever going through the OTP
   * flow, and it has to land in exactly the same place — including the storage
   * reset, so the previous user's cached data does not leak into the new one.
   */
  async adoptSession(sess: SessionDto): Promise<SessionDtoExtended> {
    const session = new SessionDtoExtended(sess);
    await this.core.storage.reset();
    await this.core.storage.setItem('live-tokens', JSON.stringify(session));
    await this.initialiseAuth();
    void this.core.api.checkSubscriptionStatus();
    return session;
  }

  async refreshTokens(): Promise<false|SessionDtoExtended> {
    const {unlock, previousParams} = await Mutex.getLock('auth-refresh-tokens').lock(false);

    if (previousParams === null) {
      console.log('No previous refresh result, trying to refresh tokens');
      let result: false|SessionDtoExtended = false;
      try {
        const tokens = await this.getLiveTokens();
        if (tokens) {
          const sess = await firstValueFrom(this.core.api.auth.authControllerRefreshToken({body: {refreshToken: tokens.refreshToken}}));
          const session = new SessionDtoExtended(sess);
          await this.core.storage.setItem('live-tokens', JSON.stringify(session));
          console.log('Stored live tokens', session, await this.core.storage.getItem('live-tokens'), await this.getLiveTokens());
          await this.initialiseAuth();
          result = session;
        }

      } catch (err) {
        console.error('Error refreshing tokens', err);
        result = false;
      } finally {
        unlock(result);
        return result;
      }
    } else {
      console.log('Returning previous refresh result from another request');
      unlock(previousParams);
      return previousParams as false|SessionDtoExtended;
    }
  }

  async logout() {
    await this.core.storage.removeItem('live-tokens');
    await this.core.storage.removeItem('last-billing-status');
    this.core.api.billingStatus$.next({isActive: false, status: 'free', freeVideosRemaining: 3});
    this.userData = undefined;
    void this.core.router.navigate(['/login']);
  }

}